import { Checkbox, Grid, Stack } from "@mui/material";
import { Label } from "./SelectAccountType";
import { BirthDateFields } from "./DateField";
import { NationalityField } from "./NationalityField";
import { MuiPhone } from "./PhoneNumberField";
import AddressLookUpComponent from "./AddressLookup";
import { FileUpload } from "./FileUploadField";
import { SocialSecurityField } from "./SocialSecurityField";
import { setIgnoreFileUpload, useOnboarding } from "src/store/features/onboarding.slice";
import PreferredNameField from "./PreferredNameField";
import api from "src/store/features/api";
import { usePrepopulateForm } from "./utils/usePrepopulateForm";
import useAutofillFromOnfido from "./utils/useAutofillFromOnfido";
import { useAppDispatch } from "src/store/store";
import { useState } from "react";

export default function AboutYou() {
  const { data: previousSubmission } = api.useGetPreviousSubmissionByEmailQuery();
  const dispatch = useAppDispatch();
  const [checked, setChecked] = useState(false);

  // Ignore File upload if document is expired or User has already uploaded the file during Biometrics.
  const { ignoreFileUpload } = useOnboarding();

  // PoA is found when document uploaded during biometris is type driver's licence.
  const { foundPoA } = useAutofillFromOnfido();

  // Prepopulate form with previous submission data
  usePrepopulateForm(previousSubmission);

  const {
    doesNotHoldSSN,
    address: { country },
    nationality,
  } = useOnboarding();

  const FileUploadMessage = () => {
    let requirePassport = false;
    const proofOfAddressMessage =
      "Proof of residential address (Drivers License, Utility bill or bank statement)";
    const passportMessage =
      "Passport information page (Clear Photo or scan of the main page with your photo and personal details)";
    if (nationality === "American") {
      requirePassport = false;
    } else {
      requirePassport =
        (country === "United States" || nationality === "American") && doesNotHoldSSN;
    }

    return (
      <>
        <Label text={proofOfAddressMessage} />
        {requirePassport && <Label text={passportMessage} />}
      </>
    );
  };

  const HideProofOfAddressDisclaimer = () => {
    const handleChange = (e: React.SyntheticEvent<Element, Event>, value: boolean) => {
      setChecked(value);
      dispatch(setIgnoreFileUpload(value));
    };
    return (
      // When PoA is found users can opt to bypass the proof of address upload
      <Stack direction="row" justifyContent="start" alignItems="center">
        <Label text="My residential address is shown on my driver licence and matches the address above" />
        <Checkbox defaultChecked={checked} onChange={handleChange} name={"hideProofOfADdress"} />
      </Stack>
    );
  };

  return (
    <Grid container spacing={1} mt={2}>
      <Grid item xs={12} sm={4} pr={{ xs: 0, sm: 1.2 }} mb={1}>
        <Stack spacing={1} data-testid="preferred-name-field">
          <Label text="Preferred Name (Optional)" />
          <PreferredNameField />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={4} pr={{ xs: 0, sm: 1.2 }} mb={1} data-testid="nationality-field">
        <Stack spacing={1}>
          <Label text="Nationality" />
          <NationalityField />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={4} mb={1} data-testid="phone-field">
        <Stack spacing={1}>
          <Label text="Mobile / Cell Number" />
          <MuiPhone />
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Label text="Birthdate" />
      </Grid>
      <Grid item xs={12} mb={1} data-testid="dob-field">
        <BirthDateFields />
      </Grid>
      <Grid item xs={12} data-testid="search-address-field">
        <Stack mb={1}>
          <Label text="Search Address" />
        </Stack>
        <Stack data-testid="manual-address-input-fields">
          <AddressLookUpComponent />
        </Stack>
      </Grid>
      <Grid item xs={12} mt={2} data-testid="social-security-field">
        <SocialSecurityField />
      </Grid>
      {foundPoA && (
        <Grid item xs={12} data-testid="poa-found-toggle">
          <HideProofOfAddressDisclaimer />
        </Grid>
      )}
      {!ignoreFileUpload && (
        <Grid item xs={12} mt={2} data-testid="file-upload-field">
          <FileUploadMessage />
          <FileUpload />
        </Grid>
      )}
    </Grid>
  );
}
